import { AttributeStore, AttributeStoreDerive } from './AttributeStore';

/**
 * {@link ImmutableAttribute} or {@link MutableAttribute}.
 */
export interface Attribute<Type> {
  /**
   * Get value of an attribute.
   * Note this usage does **not** trigger re-render of React component.
   * It should be used out of React.
   * For usage within React, use {@link useValue}.
   * @example
   * const userName = user.attributes.name.getValue();
   */
  getValue(): Type;

  /**
   * Get value of an attribute and subscribe to its changes.
   * Note this is a React hook. It can only be used within a React component, or another React hook.
   * For usage out of React, use {@link getValue}.
   * @example
   * const UserView = (props: { user: User }) => { // user is an entity
   *   const userName = props.user.attributes.name.useValue();
   * }
   */
  useValue(): Type;
}

export interface IdentifiableAttribute<Type, Id> extends Attribute<Type> {
  readonly _id: Id;
}

export const IMMUTABLE_ID_PLACEHOLDER = Symbol();

/**
 * Immutable attribute is an attribute of an entity that does not change,
 * or it changes but UI has no interest in it.
 * React component does not listen to changes of immutable attributes.
 * For example, `id` of an entity should be immutable.
 * @see {@link AttributeStore.immutable}
 * @example
 * class User { // User entity
 *   readonly attributes = {
 *     id: attributeStore.immutable(1),
 *     name: attributeStore.mutable('John'),
 *   }
 * }
 *
 * const UserView = (props: { user: User }) => { // usage in React component
 *   const userId = props.user.attributes.id.useValue();
 *   const userName = props.user.attributes.name.useValue();
 * }
 */
export class ImmutableAttribute<Type> implements Attribute<Type> {
  /**
   * This is not required by immutable attribute itself,
   * but it is used to prevent assigning an immutable attribute to a mutable attribute.
   */
  readonly _id = IMMUTABLE_ID_PLACEHOLDER;

  /** Actual initial value beneath the attribute. */
  readonly rawValue: Type;

  /**
   * Constructor of {@link ImmutableAttribute}.
   * Do not call directly. To create an immutable attribute, use {@link AttributeStore.immutable}.
   * @constructor
   */
  constructor(args: { rawValue: Type }) {
    this.rawValue = args.rawValue;
  }

  getValue() {
    return this.rawValue;
  }

  useValue() {
    return this.rawValue;
  }
}

/**
 * Mutable attribute is an attribute of an entity that can be changed by UI.
 * UI or React component listens to its changes.
 * @see {@link AttributeStore.mutable}
 * @example
 * class User { // User entity
 *   readonly attributes = {
 *     id: attributeStore.immutable(1),
 *     name: attributeStore.mutable('John'),
 *   }
 * }
 *
 * const UserView = (props: { user: User }) => { // usage in React component
 *   const userId = props.user.attributes.id.useValue();
 *   const userName = props.user.attributes.name.useValue();
 * }
 */
export class MutableAttribute<Type, Id = unknown> implements IdentifiableAttribute<Type, Id> {
  private _store: AttributeStore<Id>;
  readonly _id: Id;

  /** Actual initial value beneath the attribute. */
  readonly rawValue: Type;

  /**
   * Constructor of {@link MutableAttribute}.
   * Do not call directly. To create a mutable attribute, use {@link AttributeStore.mutable}.
   * @constructor
   */
  constructor(args: { store: AttributeStore<Id>; rawValue: Type }) {
    this._id = args.store.mutableId(args.rawValue);
    this._store = args.store;
    this.rawValue = args.rawValue;
  }

  getValue(): Type {
    return this._store.get(this._id);
  }

  /**
   * Check whether an attribute has been changed.
   * If the attribute is immutable, it simply returns `false`.
   * Otherwise, it compares the attribute's current value with its original {@link MutableAttribute#rawValue} by {@link Object.is}.
   * Note this usage does **not** trigger re-render of React component.
   * @returns `true` if the attribute has been changed, `false` otherwise.
   * @example user.attributes.userName.isDirty()
   */
  isDirty(): boolean {
    const currentValue = this.getValue();
    const originalValue = this.rawValue;
    return !Object.is(currentValue, originalValue);
  }

  useValue(): Type {
    return this._store.useAttributeValue(this._id);
  }
}

/**
 * Mutable attribute is an attribute of an entity that is derived from other attributes.
 * UI or React component listens to its changes.
 * @see {@link AttributeStore.derivable}
 * @example
 * class User { // User entity
 *   readonly id = attributeStore.immutable(1),
 *   readonly name = attributeStore.mutable('John'),
 *   readonly nameInUpperCase = attributeStore.derivable(get => get(this.name).toUpperCase());
 * }
 *
 * const UserView = (props: { user: User }) => { // usage in React component
 *   const userId = props.user.id.useValue();
 *   const userName = props.user.name.useValue();
 *   const userNameInUpperCase = props.user.nameInUpperCase.useValue();
 * }
 */
export class DerivableAttribute<Type, Id = unknown> implements IdentifiableAttribute<Type, Id> {
  private _store: AttributeStore<Id>;
  readonly _id: Id;

  /**
   * Constructor of {@link MutableAttribute}.
   * Do not call directly. To create a mutable attribute, use {@link AttributeStore.mutable}.
   * @constructor
   */
  constructor(args: { store: AttributeStore<Id>; derive: AttributeStoreDerive<Type, Id> }) {
    this._id = args.store.derivableId(args.derive);
    this._store = args.store;
  }

  useValue(): Type {
    return this._store.useAttributeValue(this._id);
  }

  getValue(): Type {
    return this._store.get(this._id);
  }
}
